import { MECHANICAL_FINDING_TYPES, FINDING_LOCATIONS, SEVERITY_LEVELS } from "@/types/arboricultural-inspection";
import type { InspectionFormState, InspectionFormAction } from "../useInspectionForm";
import { useCtaCatalogs } from "@/hooks";
import TriState from "./TriState";

interface Props {
  state: InspectionFormState;
  dispatch: React.Dispatch<InspectionFormAction>;
}

const labelCls = "text-xs font-medium text-foreground-500 mb-1 block";
const selectCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none cursor-pointer";
const inputCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors";

const MECH_TYPE_LABELS: Record<string, string> = {
  CODOMINANCE: "Codominância",
  INCLUDED_BARK: "Casca inclusa",
  BROKEN_BRANCH: "Galho quebrado",
  DEAD_BRANCH: "Galho morto",
  STRUCTURAL_CAVITY: "Cavidade estrutural",
  STRUCTURAL_CRACK: "Fissura estrutural",
  CROWN_IMBALANCE: "Desequilíbrio de copa",
  ROOT_PLATE_MOVEMENT: "Movimentação do prato de raízes",
  ANCHORAGE_LOSS: "Perda de ancoragem",
  OTHER: "Outro",
};

const LOCATION_LABELS: Record<string, string> = {
  ROOT_COLLAR: "Colo", TRUNK: "Tronco", BIFURCATION: "Bifurcação",
  BRANCH: "Galho", CROWN: "Copa", ROOT: "Raiz", OTHER: "Outro",
};

const SEVERITY_LABELS: Record<string, string> = {
  LOW: "Baixa", MODERATE: "Moderada", HIGH: "Alta", NOT_ASSESSED: "Não avaliada",
};

// Escala R1-R4 (MFI-AU). Rotulos e cuidados vem do servidor; isto so evita tela vazia.
const R_FALLBACK = [
  { code: "R1", name: "Raízes subterrâneas", caution: "Descrever como foi observado e os limites da observação." },
  { code: "R2", name: "Raízes superficiais dentro da área disponível", caution: "Caracterizar a área livre." },
  { code: "R3", name: "Raízes superficiais ultrapassando a área disponível", caution: "Documentar a extensão sem pressupor dano." },
  { code: "R4", name: "Raízes superficiais com danos ou conflitos observados", caution: "Exige foto e descrição do dano ou conflito." },
];

const ROOT_BOOLEANS: { field: string; label: string }[] = [
  { field: "exposedRoots", label: "Raízes expostas" },
  { field: "abnormalInclination", label: "Inclinação anormal" },
  { field: "underminedRoots", label: "Raízes solapadas" },
  { field: "damagedRoots", label: "Raízes danificadas" },
  { field: "constructionDamage", label: "Dano por construção" },
  { field: "soilCompaction", label: "Compactação do solo" },
  { field: "rootPlateMovement", label: "Movimentação do prato" },
];

export default function StepBiomecanica({ state, dispatch }: Props) {
  const ha = state.healthAssessment;
  const catalogs = useCtaCatalogs();
  const rOptions = catalogs?.root_classes ?? R_FALLBACK;
  const selectedR = rOptions.find((o) => o.code === ha.rootCondition) ?? null;
  const needsRootDescription = ha.rootCondition === "R3" || ha.rootCondition === "R4";

  return (
    <div className="space-y-5">
      {/* Classe radicular R1-R4 */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-plant-line text-accent-500"></i>
          Sistema radicular (R1–R4)
        </h4>
        <p className="text-xs text-foreground-500 mt-1 mb-3">
          R3 e R4 não significam, por si, risco crítico nem dano comprovado: descreva o que foi observado.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Classe radicular">
          {rOptions.map((o) => {
            const selected = ha.rootCondition === o.code;
            return (
              <button
                key={o.code}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => dispatch({ type: "SET_HEALTH", field: "rootCondition", value: selected ? null : o.code })}
                className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                  selected ? "border-primary-400 bg-primary-50" : "border-background-200/70 hover:bg-background-50"
                }`}
              >
                <span className="block text-sm font-bold text-foreground-800">{o.code}</span>
                <span className="block text-xs text-foreground-600 leading-snug">{o.name}</span>
              </button>
            );
          })}
        </div>
        {selectedR && (
          <p className="mt-3 text-xs rounded-lg bg-background-50 border border-background-200/60 px-3 py-2 text-foreground-600">
            <strong className="text-foreground-800">{selectedR.code}:</strong> {selectedR.caution}
          </p>
        )}
        {needsRootDescription && (
          <div className="mt-3">
            <label className={labelCls}>
              {ha.rootCondition === "R4" ? "Descrição do dano ou do conflito *" : "Extensão observada"}
            </label>
            <textarea
              rows={2}
              value={ha.rootNotes ?? ""}
              onChange={(e) => dispatch({ type: "SET_HEALTH", field: "rootNotes", value: e.target.value })}
              placeholder={
                ha.rootCondition === "R4"
                  ? "O que foi danificado, onde e em que extensão. Anexe foto do dano na etapa de evidências."
                  : "Até onde as raízes avançam além da área disponível."
              }
              className={`${inputCls} resize-none`}
            />
            {ha.rootCondition === "R4" && (
              <p className="mt-1 text-[11px] text-foreground-500">R4 exige esta descrição e ao menos uma foto (raízes, colo, defeito ou conflito urbano).</p>
            )}
          </div>
        )}
      </div>

      {/* Root and inclination conditions */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2 mb-3">
          <i className="ri-anchor-line text-accent-500"></i>
          Condição de Raízes e Inclinação
        </h4>
        <div className="divide-y divide-background-100 mb-3">
          {ROOT_BOOLEANS.map((b) => (
            <TriState
              key={b.field}
              label={b.label}
              value={(ha as Record<string, boolean | null | undefined>)[b.field]}
              onChange={(v) => dispatch({ type: "SET_HEALTH", field: b.field as never, value: v })}
            />
          ))}
        </div>
        {ha.abnormalInclination && (
          <div className="grid grid-cols-2 gap-3 mt-2 p-3 bg-warning-50/50 rounded-lg border border-warning-200/50">
            <div>
              <label className={labelCls}>Ângulo de inclinação (graus)</label>
              <input
                type="number"
                step="1"
                value={ha.inclinationAngleDeg ?? ""}
                onChange={(e) =>
                  dispatch({ type: "SET_HEALTH", field: "inclinationAngleDeg", value: e.target.value ? parseFloat(e.target.value) : null })
                }
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Direção da inclinação</label>
              <select
                value={ha.inclinationDirection ?? ""}
                onChange={(e) =>
                  dispatch({ type: "SET_HEALTH", field: "inclinationDirection", value: e.target.value || null })
                }
                className={selectCls}
              >
                <option value="">—</option>
                {["N", "NE", "E", "SE", "S", "SW", "W", "NW"].map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div className="mt-3">
          <label className={labelCls}>Detalhe das raízes expostas</label>
          <select
            value={ha.exposedRootsDetail ?? ""}
            onChange={(e) =>
              dispatch({ type: "SET_HEALTH", field: "exposedRootsDetail", value: e.target.value || null })
            }
            className={selectCls}
          >
            <option value="">—</option>
            <option value="SURFACE">Superficial</option>
            <option value="LIFTED_SIDEWALK">Levantando calçada</option>
            <option value="UNDERMINED">Solapadas</option>
            <option value="DAMAGED">Danificadas</option>
          </select>
        </div>
      </div>

      {/* Mechanical findings */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
            <i className="ri-tools-line text-warning-500"></i>
            Achados Biomecânicos
          </h4>
          <button
            type="button"
            onClick={() =>
              dispatch({ type: "ADD_MECHANICAL_FINDING", finding: { findingType: "OTHER" } })
            }
            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <i className="ri-add-line"></i> Adicionar achado
          </button>
        </div>

        {state.mechanicalFindings.length === 0 ? (
          <p className="text-xs text-foreground-400">Nenhum achado biomecânico registrado.</p>
        ) : (
          <div className="space-y-3">
            {state.mechanicalFindings.map((f, idx) => (
              <div key={idx} className="p-3 bg-background-50 rounded-lg border border-background-200/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground-600">Achado #{idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => dispatch({ type: "REMOVE_MECHANICAL_FINDING", index: idx })}
                    className="text-xs text-danger-500 hover:text-danger-600"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>Tipo</label>
                    <select
                      value={f.findingType}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_MECHANICAL_FINDING", index: idx, field: "findingType", value: e.target.value })
                      }
                      className={selectCls}
                    >
                      {MECHANICAL_FINDING_TYPES.map((t) => (
                        <option key={t} value={t}>{MECH_TYPE_LABELS[t] ?? t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Localização</label>
                    <select
                      value={f.location ?? ""}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_MECHANICAL_FINDING", index: idx, field: "location", value: e.target.value || null })
                      }
                      className={selectCls}
                    >
                      <option value="">—</option>
                      {FINDING_LOCATIONS.map((l) => (
                        <option key={l} value={l}>{LOCATION_LABELS[l] ?? l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Severidade</label>
                    <select
                      value={f.severity ?? ""}
                      onChange={(e) =>
                        dispatch({ type: "UPDATE_MECHANICAL_FINDING", index: idx, field: "severity", value: e.target.value || null })
                      }
                      className={selectCls}
                    >
                      <option value="">—</option>
                      {SEVERITY_LEVELS.map((s) => (
                        <option key={s} value={s}>{SEVERITY_LABELS[s] ?? s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-2">
                  <input
                    type="text"
                    value={f.notes ?? ""}
                    onChange={(e) =>
                      dispatch({ type: "UPDATE_MECHANICAL_FINDING", index: idx, field: "notes", value: e.target.value || null })
                    }
                    placeholder="Notas..."
                    className={inputCls}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
